import { NativePushToken, IPushLogger } from './types';
import { IPushTokenData, IPushTokenApi } from './pushTokenApi';
import { buildPushTokenFromNativePushToken } from './pushTokenMapping';
import { INativePushNotificationBridge, getNativeBridge } from './nativeBridge';

const NATIVE_BRIDGE_TIMEOUT_MS = 10000;

/**
 * Manages push notification tokens for mobile apps.
 * Handles fetching tokens from native bridge, registering with backend, and maintaining token state.
 */
class PushTokenService {
    private token?: NativePushToken;
    private pushToken?: IPushTokenData;
    private static instance?: PushTokenService;
    private bridge: INativePushNotificationBridge;
    private initialized: boolean = false;
    private initializationPromise?: Promise<void>;
    private api?: IPushTokenApi;
    private logger?: IPushLogger;
    private refreshPromise: Promise<void> = Promise.resolve();
    private generation = 0;

    private constructor(bridge: INativePushNotificationBridge) {
        this.bridge = bridge;
    }

    private rejectAfterTimeout(reject: (reason: Error) => void, message: string): ReturnType<typeof setTimeout> {
        return setTimeout(() => {
            reject(new Error(message));
        }, NATIVE_BRIDGE_TIMEOUT_MS);
    }

    /**
     * Initialize the push service and fetch token from native app.
     * Stores the API adapter for later registration.
     * Safe to call multiple times - will only initialize once.
     */
    async initialize(api: IPushTokenApi, logger?: IPushLogger): Promise<void> {
        if (this.initialized) {
            return;
        }

        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        this.api = api;
        this.logger = logger;
        this.initializationPromise = this.initializePushService();

        try {
            await this.initializationPromise;
            this.initialized = true;
        } catch (error) {
            this.initializationPromise = undefined;
            this.logger?.error("Push token initialization failed", error);
            throw error;
        }
    }

    private async fetchPushTokenFromNativeApp(): Promise<NativePushToken> {
        return new Promise((resolve, reject) => {
            const timeoutId = this.rejectAfterTimeout(reject, "Timeout waiting for push token from native app");

            this.bridge.getPushToken(undefined, (error: Error | undefined, result: NativePushToken | undefined) => {
                clearTimeout(timeoutId);

                if (error) {
                    this.logger?.error("Failed to fetch push token from native app", error);
                    reject(error);
                    return;
                }
                if (result) {
                    this.logger?.log("Successfully fetched push token from native app");
                    resolve(result);
                } else {
                    const err = new Error("No push token received");
                    this.logger?.error("No push token received from native app", err);
                    reject(err);
                }
            });
        });
    }

    private async startNativePushService(): Promise<void> {
        return new Promise((resolve, reject) => {
            const timeoutId = this.rejectAfterTimeout(reject, "Timeout waiting for native push service to start");

            this.bridge.startPushService(undefined, (error: Error | undefined, result: any) => {
                clearTimeout(timeoutId);

                if (error) {
                    this.logger?.error("Failed to start native push service", error);
                    reject(error);
                    return;
                }
                this.logger?.log("Native push service started successfully");
                resolve(result);
            });
        });
    }

    private async initializePushService(): Promise<void> {
        await this.startNativePushService();
        this.token = await this.fetchPushTokenFromNativeApp();
    }

    /**
     * Register the push token with the backend.
     * Must call initialize() first.
     * @param userId - The ID of the currently logged-in user
     */
    async registerPushTokenWithBackend(userId: string): Promise<void> {
        if (!this.initialized) {
            throw new Error("Must call initialize() before registering with API.");
        }

        if (!this.api) {
            throw new Error("API adapter is required. Pass it to initialize().");
        }

        const generation = this.generation;
        const registration = this.refreshPromise.then(async () => {
            if (generation !== this.generation || !this.initialized || !this.api) {
                return;
            }
            const api = this.api;
            const logger = this.logger;
            try {
                if (!this.token) {
                    throw new Error("Push token is not available yet. Please ensure initialization is complete.");
                }

                const pushToken = buildPushTokenFromNativePushToken(this.token, userId);
                // Preserve remove-by-value authorization if the first Add fails client-side.
                this.pushToken ??= pushToken;
                const registeredTokenId = await api.addPushToken(pushToken);

                pushToken.id = registeredTokenId;
                this.pushToken = pushToken;
                logger?.log("Successfully registered push token with backend", { id: registeredTokenId });
            } catch (error) {
                logger?.error("Failed to register push token with backend", error);
                throw error;
            }
        });
        this.refreshPromise = registration.catch(() => {});
        return registration;
    }

    /**
     * Re-fetch and register a rotated token, serialized with login registration.
     */
    async refreshPushTokenRegistration(userId: string): Promise<void> {
        if (!this.initialized || !this.api) {
            return;
        }

        const generation = this.generation;
        const refresh = this.refreshPromise.then(async () => {
            if (generation !== this.generation || !this.initialized || !this.api) {
                return;
            }

            const api = this.api;
            const logger = this.logger;
            const token = await this.fetchPushTokenFromNativeApp();
            if (generation !== this.generation) {
                return;
            }

            this.token = token;
            const previous = this.pushToken;
            if (previous?.id && previous.token === token.token) {
                return;
            }

            const pushToken = buildPushTokenFromNativePushToken(token, previous?.user.id ?? userId);
            this.pushToken ??= pushToken;
            const id = await api.addPushToken(pushToken);

            pushToken.id = id;
            this.pushToken = pushToken;
            logger?.log("Successfully registered push token with backend", { id });

            if (previous?.id && previous.id !== id) {
                void api.removePushToken({ id: previous.id, user: previous.user }).catch(error => {
                    logger?.error("Failed to unregister previous push token", error);
                });
            }
        });
        // A failed refresh must reject its caller without blocking subsequent refreshes.
        this.refreshPromise = refresh.catch(() => {});
        return refresh;
    }

    /**
     * Unregister the push token from the backend.
     * This soft-deletes the token by calling the Remove API method.
     * Must be called before logout to ensure the device stops receiving notifications.
     */
    async unregisterPushToken(userId?: string): Promise<void> {
        this.generation++;
        if (!this.api) {
            const err = new Error("API adapter is required. Cannot unregister without it.");
            throw err;
        }

        try {
            if (this.pushToken?.id) {
                try {
                    await this.api.removePushToken({ id: this.pushToken.id, user: this.pushToken.user });
                    this.logger?.log("Successfully unregistered push token", { id: this.pushToken.id });
                } catch (error) {
                    this.logger?.error("Failed to unregister push token", error);
                }
            }
            // Add may commit before failing; the live token can differ from the confirmed record.
            if (this.token && (!this.pushToken?.id || this.token.token !== this.pushToken.token)) {
                const user = this.pushToken?.user ?? (userId ? { id: userId } : undefined);
                if (!user) {
                    this.logger?.error("Cannot unregister push token — no user available. Token will be cleaned up by the backend cron job.");
                    return;
                }
                await this.api.removePushToken({ token: this.token.token, user });
                this.logger?.log("Successfully unregistered push token by value");
            } else if (!this.pushToken?.id) {
                throw new Error("No push token available to unregister.");
            }
        } catch (error) {
            this.logger?.error("Failed to unregister push token", error);
        } finally {
            // Always reset state, even if unregistration fails (don't block logout)
            this.logger?.log("Resetting PushTokenService singleton after unregisterPushToken");
            this.reset();
        }
    }

    /**
     * Unregister all push tokens for the current user from the backend.
     * Useful for "logout everywhere" functionality.
     * @param fallbackUserId - Optional user ID to use if pushToken.user is not available
     */
    async unregisterAllPushTokensForUser(fallbackUserId?: string): Promise<void> {
        if (!this.api) {
            throw new Error("API adapter is required. Cannot unregister without it.");
        }

        const userId = this.pushToken?.user?.id ?? fallbackUserId;

        if (!userId) {
            this.logger?.error("No user information available to unregister tokens", new Error("No user ID"));
            this.reset();
            return;
        }

        const userTokens = await this.api.getPushTokensForUser(userId);

        const errors: Error[] = [];

        for (const token of userTokens) {
            try {
                await this.api.removePushToken({ id: token.id, token: token.token, user: token.user });
            } catch (error) {
                errors.push(error instanceof Error ? error : new Error(String(error)));
            }
        }

        this.reset();

        if (errors.length > 0) {
            const errorMessage = `Failed to remove ${errors.length} of ${userTokens.length} push token(s). ` +
                `Errors: ${errors.map(e => e.message).join(', ')}`;
            const err = new Error(errorMessage);
            this.logger?.error("Failed to unregister all push tokens", err);
        } else {
            this.logger?.log(`Successfully unregistered all ${userTokens.length} push tokens for user`);
        }
    }

    /**
     * Reset the service state.
     * Clears all stored tokens and initialization flags.
     * Also resets the singleton instance so a fresh instance is created on next use.
     * Useful for logout or when switching users.
     * @internal - Only for internal cleanup and testing, not meant to be called externally
     */
    reset(): void {
        this.generation++;
        // Clear instance properties first before resetting singleton
        this.token = undefined;
        this.pushToken = undefined;
        this.initialized = false;
        this.initializationPromise = undefined;
        this.api = undefined;
        this.logger = undefined;

        // Reset the singleton instance - triggers garbage collection of the old instance
        PushTokenService.instance = undefined;
    }

    /**
     * Get the current push token.
     * Returns undefined if not yet initialized.
     */
    getPushToken(): IPushTokenData | undefined {
        return this.pushToken;
    }

    /**
     * Get the native push token.
     * Returns undefined if not yet initialized.
     */
    getNativeToken(): NativePushToken | undefined {
        return this.token;
    }

    /**
     * Check if the service has been initialized.
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Check if initialization is currently in progress.
     */
    isInitializationPending(): boolean {
        return !this.initialized && this.initializationPromise !== undefined;
    }

    /**
     * Check if native bridge is available.
     * Returns true if running in a mobile app environment with push notification support.
     */
    static hasNativeBridge(): boolean {
        return getNativeBridge() !== undefined;
    }

    /**
     * Get the singleton instance of PushTokenService.
     * Throws error if push notification service is not available.
     */
    static getInstance(): PushTokenService {
        if (!PushTokenService.instance) {
            const bridge = getNativeBridge();
            if (!bridge) {
                throw new Error("Push notification service is not available. This feature is only available in mobile app environments.");
            }
            PushTokenService.instance = new PushTokenService(bridge);
        }
        return PushTokenService.instance;
    }

    /**
     * Create a new instance with custom bridge (useful for testing).
     * @internal
     */
    static createInstance(bridge: INativePushNotificationBridge): PushTokenService {
        return new PushTokenService(bridge);
    }

    /**
     * Reset the singleton instance (useful for testing).
     * Also clears the current instance's state, which makes any of its pending work stale.
     * @internal
     */
    static resetInstance(): void {
        PushTokenService.instance?.reset();
        PushTokenService.instance = undefined;
    }
}

export { PushTokenService };
export default PushTokenService;
